import React, { useEffect, useRef, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { MailX, PenLine, X } from "lucide-react";
import { api } from "../../lib/api";
import MailReader from "../../pages/communication/MailReader";
import { RequestUpdateSheet } from "./RequestUpdateSheet";

/**
 * Handles Communication Centre deep-links (`?cc=1&action=reply|chase&blocker=…`) once the room has
 * finished loading. Reply opens the existing mail composer on the latest received email linked to this
 * deal; Chase opens the reviewable Request Update draft. Nothing is ever sent without the user pressing Send.
 */
export const CommunicationDeepLink = ({ dealId, roomLoaded, blockers, participants = [], onLogUpdate, onRequestDoc }) => {
  const [params, setParams] = useSearchParams();
  const [state, setState] = useState(null); // {kind: "reply"|"chase"|"no-message", ...}
  const handled = useRef(false);
  const action = params.get("cc") === "1" ? params.get("action") : null;
  const blockerId = params.get("blocker");

  useEffect(() => { handled.current = false; }, [dealId, action, blockerId]);
  useEffect(() => {
    if (!action || !roomLoaded || handled.current) return;
    handled.current = true;
    const clear = () => {
      const next = new URLSearchParams(params);
      ["cc", "action", "blocker"].forEach((k) => next.delete(k));
      setParams(next, { replace: true });
    };
    const blocker = (blockers || []).find((b) => b.id === blockerId)
      || (blockerId ? { id: blockerId, title: "Request update" } : null);
    if (action === "chase") {
      if (blocker) setState({ kind: "chase", blocker });
      clear();
      return;
    }
    if (action === "request-doc") {
      // Preselect the person responsible for the open item (same resolution as Chase); else leave blank.
      const resolve = blocker
        ? api.post(`/api/v2/deals/${dealId}/blockers/${blocker.id}/request-update/preview`, {})
          .then(({ data }) => data?.recipient?.email || null).catch(() => null)
        : Promise.resolve(null);
      resolve.then((email) => { clear(); onRequestDoc?.(email); });
      return;
    }
    if (action === "call") { clear(); onLogUpdate?.("call"); return; }  // composer opens on "Call"
    if (action !== "reply") { clear(); return; }
    api.get(`/api/v2/mailbox/inbox?tab=all&deal_id=${dealId}&limit=25`)
      .then(({ data }) => {
        const msg = (data?.messages || []).find((m) => m.direction !== "outbound");
        setState(msg ? { kind: "reply", message: msg }
          : { kind: "no-message", connected: !!data?.provider_connected, blocker });
      })
      .catch(() => setState({ kind: "no-message", connected: false, blocker }))
      .finally(clear);
  }, [action, roomLoaded, blockers, blockerId, dealId, params, setParams]); // eslint-disable-line react-hooks/exhaustive-deps

  if (!state) return null;
  const close = () => setState(null);
  if (state.kind === "reply") {
    return <MailReader message={state.message} initialMode="reply" onClose={close} />;
  }
  if (state.kind === "chase") {
    return <RequestUpdateSheet dealId={dealId} blocker={state.blocker} participants={participants} onClose={close} onSent={close} />;
  }
  return (
    <NoMessageDialog
      connected={state.connected}
      canDraft={!!state.blocker}
      onClose={close}
      onNewMessage={() => setState({ kind: "chase", blocker: state.blocker })}
      onLogUpdate={() => { close(); onLogUpdate?.(null); }}
    />
  );
};

const NoMessageDialog = ({ connected, canDraft, onClose, onNewMessage, onLogUpdate }) => (
  <div className="fixed inset-0 z-[70] flex items-center justify-center bg-slate-900/40 p-4" onClick={onClose} data-testid="cc-reply-no-message">
    <div className="w-full max-w-[420px] rounded-2xl bg-white shadow-2xl p-5" onClick={(e) => e.stopPropagation()} role="dialog" aria-modal="true">
      <div className="flex items-start gap-3">
        <div className="h-8 w-8 rounded-lg bg-slate-100 text-slate-500 flex items-center justify-center shrink-0">
          <MailX size={15} strokeWidth={2.2} />
        </div>
        <div className="min-w-0 flex-1">
          <h2 className="text-[14.5px] font-bold text-slate-900">No message to reply to</h2>
          <p className="mt-1 text-[12.5px] text-slate-600 leading-relaxed" data-testid="cc-reply-no-message-reason">
            {connected
              ? "No received email is linked to this deal yet."
              : "No mailbox is connected, so there are no received emails for this deal."}
          </p>
        </div>
        <button type="button" onClick={onClose} aria-label="Close" className="h-7 w-7 rounded-md text-slate-400 hover:bg-slate-100 hover:text-slate-700 flex items-center justify-center" data-testid="cc-reply-no-message-close">
          <X size={15} />
        </button>
      </div>
      <div className="mt-4 flex flex-wrap justify-end gap-2">
        <button type="button" onClick={onLogUpdate} className="h-9 px-3 rounded-lg ring-1 ring-slate-200 text-[12.5px] font-semibold text-slate-700 hover:bg-slate-50" data-testid="cc-reply-log-update">
          Log an update
        </button>
        {canDraft && (
          <button type="button" onClick={onNewMessage} className="inline-flex items-center gap-1.5 h-9 px-3 rounded-lg bg-violet-600 text-white text-[12.5px] font-semibold hover:bg-violet-700" data-testid="cc-reply-new-message">
            <PenLine size={13} strokeWidth={2.3} /> New message to owner
          </button>
        )}
      </div>
    </div>
  </div>
);
